import { NonTaxableIncome } from '@/apis/family_application/requests'
import {
  selectFamilyApplication,
  setApplicationDocument,
  UpdateIncomeDetails
} from '@/app-storage/applications/familyApplicationSlice'
import { useAppAlert } from '@/app/AppAlertProvider'
import { AppRoutePaths } from '@/routing/route_paths'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { AppDispatch, RootState } from '@/storage/store'
import Disabilitymage from '@assets/disability.png'
import Form1040 from '@assets/form_1040.png'
import Form1099 from '@assets/form_1099.png'
import FormW2 from '@assets/form_w2.jpg'
import SSNImage from '@assets/ssn_iamge.png'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { TaxForms } from '../IncomeVerificationWizardProvider'
import useUploadParams from './useUploadParams'

const socialSecurityInfo = {
  name: 'social_security',
  code: 'social_security',
  longName: 'social_security_award_letter',
  image: SSNImage,
  done: false,
} as const
const disabilityBenefitsInfo = {
  name: 'disability_benefits',
  longName: 'disability_award_letter',
  code: 'disability_benefits',
  image: Disabilitymage,
  done: false,
} as const
const childSupportPaymentsInfo = {
  name: 'child_support_payments',
  longName: 'child_support_payments',
  code: 'child_support_payments',
  image: Disabilitymage,
  done: false,
} as const
const alimonyPaymentsInfo = {
  name: 'alimony_payments',
  longName: 'alimony_payments',
  code: 'alimony_payments',
  image: Disabilitymage,
  done: false,
}
const otherInfo = {
  name: 'other',
  longName: 'other',
  code: 'other',
  image: Disabilitymage,
  done: false,
} as const

const form1099 = {
  name: 'form_1099',
  longName: 'form_1099',
  code: 'form_1099',
  description: 'form_1099_text',
  image: Form1099,
  done: false,
}

const w2 = {
  name: 'form_w2',
  code: 'form_w2',
  longName: 'form_w2',
  description: 'form_w2_text',
  image: FormW2,
  done: false,
} as const

const form1040 = {
  name: 'form_1040',
  longName: 'form_1040',
  code: 'form_1040',
  description: 'form_1040_text',
  image: Form1040,
  done: false,
} as const

const formNonTaxable = {
  name: 'non_taxable',
  code: 'non_taxable',
  description: 'non_taxable_text',
  image: Disabilitymage,
  longName: 'non_taxable',
  done: false,
}

const { select_household_member } =
  AppRoutePaths.family_application.income_verification

function useTaxableIncomeDocumentParams() {
  const { fireAlert } = useAppAlert()
  const programId = useSelector(selectProgramID)
  const { selectedMember, selectedOption, updateParams } = useUploadParams()

  const dispatch = useDispatch<AppDispatch>()
  const application = useSelector(selectFamilyApplication)
  const programSettings = useSelector(
    (state: RootState) => state.ProgramSettings
  )
  const navigate = useNavigate()

  async function handleIndividuallyFiledTaxes() {
    const userDocuments = application?.documents.some(
      (document) => document.documentMember === 'user'
    )
    const spouseDocuments = application?.documents.some(
      (document) => document.documentMember === 'partner'
    )

    // ensure both partners have filled out their tax forms before they can proceed.

    if (!userDocuments || !spouseDocuments) {
      updateParams(select_household_member)
    } else {
      await dispatch(
        UpdateIncomeDetails({
          program_id: programId,
          skip_income_verification: false,
        })
      )
      updateParams(
        AppRoutePaths.family_application.income_verification.attestations
      )
    }
  }

  async function checkIncomeComplete() {
    if (application?.skip_income_verification) {
      if (
        application.household_inhabitants &&
        application.household_income_greater !== null &&
        application.household_income_type
      ) {
        const ActionResult = await dispatch(
          UpdateIncomeDetails({
            skip_income_verification: false,
            program_id: Number(programSettings.data?.id),
          })
        )

        if (!UpdateIncomeDetails.fulfilled.match(ActionResult)) {
          await fireAlert({
            variant: 'error',
            message: ActionResult.payload?.message,
          })
        }
      }
    }
  }

  async function onFormSubmitted() {
    switch (application?.household_filed_federal_income) {
      case 'single':
      case 'jointly':
      case 'did_not_file':
        await dispatch(
          UpdateIncomeDetails({
            program_id: programId,
            skip_income_verification: false,
          })
        )
        navigate(
          AppRoutePaths.family_application.income_verification.attestations
        )
        return
      case 'individually':
        handleIndividuallyFiledTaxes()
        return
      default:
        break
    }
  }

  function onEnterForm(route: string, type: TaxForms | NonTaxableIncome) {
    updateParams(route, { selectedOption: type })
  }

  function saveImage(image: string) {
    dispatch(
      setApplicationDocument({
        // temporarily use fallbacks here. They should never be undefined.
        form: selectedOption ?? 'social_security',
        src: image,
        member: selectedMember ?? 'user',
      })
    )
  }

  function onTaxFormSelect(value: TaxForms) {
    updateParams(
      AppRoutePaths.family_application.income_verification.capture_instructions,
      {
        selectedOption: value,
        selectedMember: selectedMember,
      }
    )
  }

  function onSpouseOrUserSelect(value: 'user' | 'partner') {
    const path =
      AppRoutePaths.family_application.income_verification.capture_instructions

    updateParams(path, {
      selectedMember: value,
      selectedOption: null,
    })
  }

  function getSavedImage() {
    const document = application?.documents.find((document) => {
      if (
        document.documentMember === selectedMember &&
        document.document_type === selectedOption
      ) {
        return document
      }
      if (!selectedMember && document.document_type === selectedOption) {
        return document
      }

      if (
        !selectedMember &&
        !selectedOption &&
        document.document_type === info().code
      ) {
        return document
      }
    })

    return document
  }

  function onRouteChange(route: string) {
    updateParams(route, {
      selectedOption: selectedOption,
      selectedMember: selectedMember,
    })
  }
  const info = () => {
    switch (selectedOption) {
      case 'social_security':
        return socialSecurityInfo
      case 'disability_benefits':
        return disabilityBenefitsInfo
      case 'child_support_payments':
        return childSupportPaymentsInfo
      case 'alimony_payments':
        return alimonyPaymentsInfo
      case 'other':
        return otherInfo
      case 'form_1099':
        return form1099
      case 'form_1040':
        return form1040
      case 'form_w2':
        return w2
      case 'nontaxable':
        return formNonTaxable
      default:
        return form1040
    }
  }

  return {
    selectedOption,
    onRouteChange,
    info,
    onFormSubmitted,
    onEnterForm,
    document,
    getSavedImage,
    saveImage,
    onSpouseOrUserSelect,
    onTaxFormSelect,
    checkIncomeComplete,
  }
}

export default useTaxableIncomeDocumentParams
